import { supabase } from "../supabaseClient";

export interface Birth {
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  lat: number;
  lon: number;
  tz: number; // hours, e.g. 5.5
}

/* Client cache. The API guide makes this mandatory, not optional: there is no
   server-side cache yet, and the in-instance rate limiter is documented as
   ineffective, so every uncached call is a full chart computation upstream.
   Caching here is the only thing keeping that load sane.
   Keyed on calculator + birth details, so two people never share an entry. */
const NS = "auraCalc:";
const DAY = 86_400_000;

/* Anything evaluated against today rather than against the birth alone has to
   expire daily, or a returning visitor gets last month's answer presented as
   current. Everything else is fixed at birth and can sit for the default. */
const TTL: Record<string, number> = {
  sade_sati: DAY,
  dasha: DAY,
  horoscope: DAY,
  horoscope_app: DAY,
  transit: DAY,
};
const DEFAULT_TTL = 30 * DAY;

const keyFor = (calculator: string, b: Birth) =>
  `${NS}${calculator}:${b.date}:${b.time}:${b.lat.toFixed(4)}:${b.lon.toFixed(4)}:${b.tz}`;

function readCache<T>(key: string, ttl: number): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { at, value } = JSON.parse(raw);
    if (Date.now() - at > ttl) {
      localStorage.removeItem(key);
      return null;
    }
    return value as T;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), value }));
  } catch {
    /* private mode or quota. Not worth failing the calculation over. */
  }
}

export async function runCalculator<T>(
  calculator: string,
  birth: Birth
): Promise<T> {
  const key = keyFor(calculator, birth);
  const ttl = TTL[calculator] ?? DEFAULT_TTL;

  const hit = readCache<T>(key, ttl);
  if (hit) return hit;

  const { data, error } = await supabase.functions.invoke("calculators", {
    body: { calculator, birth },
  });

  if (error) throw new Error(error.message || "Could not reach the calculator.");
  if (data?.error) throw new Error(data.error);
  if (!data?.result) throw new Error("The calculator returned nothing.");

  writeCache(key, data.result);
  return data.result as T;
}

/* gun_milan is the one calculator that takes two births rather than one, so it
   needs its own call and its own cache key. Order matters: several kootas are
   directional, so boy+girl and girl+boy are genuinely different readings and
   must not share an entry. Depends only on the two births, so it never
   expires. */
export async function runGunMilan<T>(boy: Birth, girl: Birth): Promise<T> {
  const part = (b: Birth) =>
    `${b.date}:${b.time}:${b.lat.toFixed(4)}:${b.lon.toFixed(4)}:${b.tz}`;
  const key = `${NS}gun_milan:${part(boy)}|${part(girl)}`;

  const hit = readCache<T>(key, DEFAULT_TTL);
  if (hit) return hit;

  const { data, error } = await supabase.functions.invoke("calculators", {
    body: { calculator: "gun_milan", boy, girl },
  });

  if (error) throw new Error(error.message || "Could not reach the calculator.");
  if (data?.error) throw new Error(data.error);
  if (!data?.result) throw new Error("The calculator returned nothing.");

  writeCache(key, data.result);
  return data.result as T;
}

/* Geoapify gives an IANA zone name. The API wants a numeric offset, and it has
   to be the offset that applied ON THE BIRTH DATE, not today: India has not
   moved, but plenty of places have, and a 1970s birth in a DST country is an
   hour out otherwise. */
export function tzOffsetHours(ianaName: string, isoDate: string): number {
  try {
    const at = new Date(`${isoDate}T12:00:00Z`);
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: ianaName,
      timeZoneName: "longOffset",
    });
    const part = fmt
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName")?.value;
    const m = part?.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!m) return 5.5;
    const sign = m[1] === "-" ? -1 : 1;
    return sign * (Number(m[2]) + Number(m[3]) / 60);
  } catch {
    return 5.5;
  }
}
